import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const BusinessValuation = () => {
  // State variables for Business Valuation calculation
  const [ebitda, setEbitda] = useState(0); // Earnings Before Interest, Tax, Depreciation, and Amortization
  const [multiple, setMultiple] = useState(0); // Industry-specific multiple
  const [businessValue, setBusinessValue] = useState(0); // Calculated business value

  // Event handlers to update state values
  const handleEbitdaChange = (e) => setEbitda(parseFloat(e.target.value) || 0);
  const handleMultipleChange = (e) => setMultiple(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate business value when any input changes
  useEffect(() => {
    if (ebitda && multiple) {
      const value = ebitda * multiple; // Calculate business value
      setBusinessValue(value);
    } else {
      setBusinessValue(0);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [ebitda, multiple]);

  return (
    <Layout title="Business Valuation Calculator" path="business-valuation">
      <section className="section-padding" id="business-valuation-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Business Valuation Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Business Valuation Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* EBITDA Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="ebitda" className="form-label">
                        EBITDA (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>EBITDA:</strong> Enter the company's Earnings Before Interest, Taxes, Depreciation, and Amortization."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="ebitda"
                        placeholder="Enter EBITDA"
                        value={ebitda || ""}
                        onChange={handleEbitdaChange}
                      />
                    </div>

                    {/* Multiple Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="multiple" className="form-label">
                        Industry Multiple
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Industry Multiple:</strong> Enter the valuation multiple specific to the industry, usually determined based on market trends."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="multiple"
                        placeholder="Enter industry multiple"
                        value={multiple || ""}
                        onChange={handleMultipleChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Business Valuation:{" "}
                        <span className="fw-bold ms-3">
                          {businessValue === 0 ? "0.00" : businessValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Business Valuation Calculator
                </h3>

                {/* What is a Business Valuation Calculator? */}
                <h4 className="mt-3 mb-3">What is a Business Valuation Calculator?</h4>
                <p>
                  A **Business Valuation Calculator** helps estimate the market value of a business based on two key inputs: EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) and an industry-specific multiple. This tool is widely used in mergers, acquisitions, and investment decisions to get an accurate valuation of a business.
                </p>

                {/* Why Use a Business Valuation Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Business Valuation Calculator?</h4>
                <p>
                  This calculator is essential for business owners, investors, and corporate finance teams to assess the potential worth of a company in the market. It provides a straightforward way to estimate how much a business might sell for based on its financial performance and industry standards.
                </p>

                {/* Benefits of Using a Business Valuation Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Business Valuation Calculator</h4>
                <ul>
                  <li><strong>Aids in understanding business worth for sale or merger:</strong> Helps you evaluate the market value of your business if you are considering selling or merging.</li>
                  <li><strong>Useful for investors considering acquisitions:</strong> Helps investors estimate the value of a business before making a purchase decision.</li>
                  <li><strong>Assists in strategic planning:</strong> Provides insights into the business's financial health, aiding in long-term strategic decisions.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Business Valuation Calculator?</h4>
                <p>
                  The Business Valuation Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Investors:</strong> Helps investors determine whether a business is a good investment based on its market value.</li>
                  <li><strong>Entrepreneurs:</strong> Assists business owners in understanding their company’s value for potential sale or strategic growth.</li>
                  <li><strong>Corporate Finance:</strong> Useful for finance teams in corporate mergers and acquisitions, or when assessing company performance in the market.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is EBITDA and why is it important?</h4>
                <p>
                  EBITDA is a measure of a company's operating performance, representing earnings before interest, taxes, depreciation, and amortization. It provides a clearer picture of a company's profitability by excluding non-operating expenses and other factors. This makes EBITDA an essential metric for valuing businesses.
                </p>

                <h4 className="mt-4 mb-3">How do I determine the industry multiple?</h4>
                <p>
                  The industry multiple can vary depending on the market, the size of the company, and other factors. It is typically derived from similar companies within the same industry or sector. It can be found through market reports, business brokers, or by comparing the multiples of companies in the same industry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BusinessValuation;
